import React, { useState, useMemo, useEffect } from 'react';
import { api } from '../../src/services/api-new';
import { allProducts as mockProducts, productCategories as mockCategories } from '../../data/products';

interface Product {
    id: number;
    name: string;
    category: string;
    price: number;
    rating?: number;
    reviews?: number;
    image: string;
    tag?: string;
}

interface ShopProps {
    products?: Product[];
    onAddToCart: (product: any) => void;
    onBack?: () => void;
}

const ShopCatalog: React.FC<ShopProps> = ({ products: externalProducts, onAddToCart, onBack }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCats, setSelectedCats] = useState<string[]>([]);
    const [allProducts, setAllProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [isDataLoading, setIsDataLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (externalProducts && externalProducts.length > 0) {
                setAllProducts(externalProducts);
                const uniqueCategories = [...new Set(externalProducts.map(p => p.category))];
                setCategories(uniqueCategories);
                setIsDataLoading(false);
                return;
            }

            setIsDataLoading(true);
            try {
                const [productsData, categoriesData] = await Promise.all([
                    api.products.getAll({ forSale: true }),
                    api.products.getCategories()
                ]);
                setAllProducts(productsData.products || productsData);
                setCategories(categoriesData);
            } catch (error) {
                console.error("Failed to fetch shop data, using mock:", error);
                setAllProducts(mockProducts);
                setCategories(mockCategories);
            } finally {
                setIsDataLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredProducts = useMemo(() => {
        return allProducts.filter(p => {
            const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.category.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCat = selectedCats.length === 0 || selectedCats.includes(p.category);
            return matchesSearch && matchesCat;
        });
    }, [searchQuery, selectedCats, allProducts]);

    const toggleCategory = (cat: string) => {
        setSelectedCats(prev =>
            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
        );
    };

    if (isDataLoading) {
        return (
            <div className="h-full flex items-center justify-center bg-surface-light dark:bg-background-dark">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Restocking Shelves...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-surface-light dark:bg-background-dark p-6 lg:p-8 scroll-smooth">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Header & Search */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 pb-2 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-4">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 hover:border-primary transition-all shadow-sm"
                            >
                                <span className="material-icons-round text-gray-600 dark:text-gray-300">arrow_back</span>
                            </button>
                        )}
                        <div className="space-y-1">
                            <h1 className="text-4xl font-black text-primary-dark dark:text-white tracking-tight">Shop Catalog</h1>
                            <p className="text-gray-500 font-medium">Curated essentials for your best friends.</p>
                        </div>
                    </div>

                    <div className="relative w-full md:w-80 group">
                        <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary transition-colors">search</span>
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all shadow-sm"
                        />
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-10">
                    {/* Sidebar Filters */}
                    <aside className="w-full lg:w-64 shrink-0 space-y-8 h-fit lg:sticky lg:top-8">
                        <div className="bg-white dark:bg-surface-dark p-6 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
                            <div>
                                <h3 className="font-black text-sm uppercase tracking-widest mb-4 text-primary-dark dark:text-gray-300">Categories</h3>
                                <div className="space-y-3">
                                    {categories.map(cat => (
                                        <label key={cat} className="flex items-center gap-3 cursor-pointer group">
                                            <div className="relative flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCats.includes(cat)}
                                                    onChange={() => toggleCategory(cat)}
                                                    className="peer h-5 w-5 rounded-lg border-gray-200 dark:border-gray-700 text-primary focus:ring-primary/20 appearance-none bg-gray-50 dark:bg-background-dark border cursor-pointer checked:bg-primary checked:border-primary transition-all shadow-sm"
                                                />
                                                <span className="material-icons-round absolute left-0.5 top-0.5 text-white text-[16px] pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity">done</span>
                                            </div>
                                            <span className={`text-sm font-bold transition-all ${selectedCats.includes(cat) ? 'text-primary' : 'text-gray-500 dark:text-gray-400 group-hover:text-primary'}`}>
                                                {cat}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="pt-6 border-t border-gray-50 dark:border-gray-800">
                                <button
                                    onClick={() => { setSelectedCats([]); setSearchQuery(''); }}
                                    className="w-full py-3 rounded-xl border border-gray-100 dark:border-gray-700 text-xs font-bold text-gray-400 hover:text-red-500 hover:border-red-100 dark:hover:border-red-900/30 transition-all flex items-center justify-center gap-2"
                                >
                                    <span className="material-icons-round text-sm">refresh</span>
                                    Clear All Filters
                                </button>
                            </div>
                        </div>
                    </aside>

                    {/* Product Grid */}
                    <div className="flex-1 space-y-6">
                        <div className="flex justify-between items-center text-sm font-bold text-gray-400">
                            <p>Showing <span className="text-primary-dark dark:text-white">{filteredProducts.length}</span> results</p>
                        </div>

                        {filteredProducts.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                                {filteredProducts.map(product => (
                                    <div key={product.id} className="group bg-white dark:bg-surface-dark rounded-[2.5rem] border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.08)] dark:hover:shadow-none transition-all flex flex-col hover:border-primary/20 animate-fade-in-up">
                                        <div className="relative aspect-square overflow-hidden bg-surface-light dark:bg-background-dark">
                                            {product.tag && (
                                                <div className="absolute top-4 left-4 bg-primary text-white text-[10px] font-black px-3 py-1.5 rounded-full z-10 shadow-lg tracking-widest">
                                                    {product.tag}
                                                </div>
                                            )}
                                            <img
                                                src={product.image}
                                                alt={product.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                loading="lazy"
                                            />
                                            <button className="absolute top-4 right-4 bg-white/80 dark:bg-surface-dark/80 backdrop-blur-md p-2.5 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500">
                                                <span className="material-icons-round">favorite_border</span>
                                            </button>
                                        </div>
                                        <div className="p-6 pt-5 flex flex-col flex-1">
                                            <div className="mb-4">
                                                <p className="text-[10px] text-primary font-black uppercase tracking-[0.2em] mb-2">{product.category}</p>
                                                <h3 className="font-black text-xl mb-2 text-primary-dark dark:text-white leading-tight group-hover:text-primary transition-colors">{product.name}</h3>
                                                <div className="flex items-center gap-1.5">
                                                    <div className="flex">
                                                        {[...Array(5)].map((_, i) => (
                                                            <span key={i} className={`material-icons-round text-[16px] ${i < Math.floor(product.rating || 0) ? 'text-yellow-400' : 'text-gray-200'}`}>star</span>
                                                        ))}
                                                    </div>
                                                    <span className="text-[11px] font-bold text-gray-400 ml-1">({product.reviews || 0} reviews)</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between mt-auto pt-6 border-t border-gray-50 dark:border-gray-800">
                                                <div className="flex flex-col">
                                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-tighter">Price</span>
                                                    <span className="font-black text-2xl text-primary-dark dark:text-white">${product.price.toFixed(2)}</span>
                                                </div>
                                                <button
                                                    onClick={() => onAddToCart(product)}
                                                    className="bg-primary hover:bg-primary-dark text-white w-14 h-14 rounded-2xl transition-all shadow-xl shadow-primary/20 flex items-center justify-center transform active:scale-90"
                                                    title="Add to Cart"
                                                >
                                                    <span className="material-icons-round text-2xl">add_shopping_cart</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-32 text-center bg-white dark:bg-surface-dark rounded-[3rem] border-2 border-dashed border-gray-100 dark:border-gray-800">
                                <div className="w-20 h-20 bg-gray-50 dark:bg-background-dark rounded-full flex items-center justify-center mx-auto mb-6">
                                    <span className="material-icons-round text-4xl text-gray-300">search_off</span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No products found</h3>
                                <p className="text-gray-500 max-w-xs mx-auto">Try adjusting your filters or search terms to find what you're looking for.</p>
                                <button
                                    onClick={() => { setSelectedCats([]); setSearchQuery(''); }}
                                    className="mt-8 text-primary font-bold hover:underline underline-offset-4"
                                >
                                    Reset all filters
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShopCatalog;
